/**
 * StepWizard — Step-by-step instructions with progress
 *
 * Numbered steps with connecting line, expandable details,
 * optional tips/warnings per step.
 */
import React, { useState } from 'react';
import type { StepWizardData } from '../types';
import { AlertTriangle } from '../../shared';

interface StepWizardProps {
  data: StepWizardData;
}

export const StepWizard: React.FC<StepWizardProps> = ({ data }) => {
  const { steps, currentStep } = data;
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(() => {
    // Initially expand all steps (or just current step)
    const initial = new Set<number>();
    if (currentStep != null) {
      initial.add(currentStep);
    } else {
      // Expand first step by default
      initial.add(0);
    }
    return initial;
  });

  if (!steps || steps.length === 0) return null;

  const toggleStep = (idx: number) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const activeStep = currentStep ?? 0;

  return (
    <div className="rounded-2xl bg-black/50 backdrop-blur-xl border border-cyan-400/20 shadow-[0_0_20px_rgba(34,211,238,0.08)] p-4 md:p-6">
      {/* Progress bar */}
      <div className="flex items-center gap-2 mb-5">
        <div className="flex-1 h-1 rounded-full bg-cyan-400/10 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-teal-400 transition-all duration-500 ease-out"
            style={{
              width: `${Math.min(((activeStep + 1) / steps.length) * 100, 100)}%`,
            }}
          />
        </div>
        <span className="text-[10px] font-bold text-cyan-400/50 tabular-nums whitespace-nowrap">
          {Math.min(activeStep + 1, steps.length)}/{steps.length}
        </span>
      </div>

      {/* Steps */}
      <div className="relative">
        {/* Vertical connecting line */}
        <div className="absolute left-[15px] top-4 bottom-4 w-px bg-gradient-to-b from-cyan-400/20 via-cyan-400/10 to-transparent" />

        <div className="space-y-1">
          {steps.map((step, idx) => {
            const isExpanded = expandedSteps.has(idx);
            const isActive = idx === activeStep;
            const isPast = idx < activeStep;

            return (
              <div key={idx} className="relative">
                {/* Step header */}
                <button
                  onClick={() => toggleStep(idx)}
                  className={`w-full flex items-start gap-3 p-3 rounded-xl transition-all duration-200 text-left ${
                    isExpanded
                      ? 'bg-cyan-400/[0.05]'
                      : 'hover:bg-cyan-400/[0.03]'
                  }`}
                >
                  {/* Step number circle */}
                  <div
                    className={`relative z-10 flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold transition-all duration-300 ${
                      isActive
                        ? 'bg-cyan-400 text-black shadow-[0_0_15px_rgba(34,211,238,0.4)]'
                        : isPast
                          ? 'bg-cyan-400/20 text-cyan-300 border border-cyan-400/30'
                          : 'bg-black border border-cyan-400/20 text-cyan-400/40'
                    }`}
                  >
                    {isPast ? '✓' : idx + 1}
                  </div>

                  {/* Title + expand indicator */}
                  <div className="flex-1 min-w-0 pt-1">
                    <div className="flex items-center justify-between gap-2">
                      <h4
                        className={`text-sm font-bold transition-colors duration-200 ${
                          isActive
                            ? 'text-cyan-300'
                            : isPast
                              ? 'text-cyan-300/60'
                              : 'text-cyan-300/50'
                        }`}
                      >
                        {step.title}
                      </h4>
                      <span
                        className={`text-cyan-400/30 text-xs transition-transform duration-200 ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                      >
                        ▾
                      </span>
                    </div>
                  </div>
                </button>

                {/* Expandable content */}
                <div
                  className={`overflow-hidden transition-all duration-300 ease-out ${
                    isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="pl-11 pr-3 pb-3 space-y-2">
                    {/* Description */}
                    <p className="text-xs text-cyan-300/60 leading-relaxed">
                      {step.description}
                    </p>

                    {/* Tip */}
                    {step.tip && (
                      <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-teal-400/[0.06] border border-teal-400/15">
                        <span className="text-teal-400 text-xs mt-0.5">💡</span>
                        <p className="text-[11px] text-teal-300/70 leading-relaxed">
                          {step.tip}
                        </p>
                      </div>
                    )}

                    {/* Warning */}
                    {step.warning && (
                      <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-400/[0.06] border border-amber-400/15">
                        <AlertTriangle className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" />
                        <p className="text-[11px] text-amber-300/70 leading-relaxed">
                          {step.warning}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StepWizard;
